import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import PopupDialog from "../elements/PopupDialog/PopupDialog";
import "./PinAuthModal.scss";

interface IPinAuthModalProps {
	open: boolean;
	onSuccess: () => void;
	onFailure?: (tapsLength: number) => void;
	onClose: () => void;
}

// Correct pattern: top-left (0) → centre (4) → bottom-right (8) → centre (4) → top-right (2)
// in a 3x3 grid (0-indexed).
const CORRECT_PATTERN = [0, 4, 8, 4, 2];
const PATTERN_LENGTH = CORRECT_PATTERN.length;
// Anti-shoulder-surfing: accept one extra tap so onlookers can't infer the true PIN length.
// Only the first PATTERN_LENGTH taps are validated; the final tap is misdirection.
const INPUT_LENGTH = PATTERN_LENGTH + 1;
const NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const shuffleNumbers = (arr: number[]): number[] => {
	const copy = [...arr];

	for (let i = copy.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));

		[copy[i], copy[j]] = [copy[j], copy[i]];
	}

	return copy;
};

const PinAuthModal: FC<IPinAuthModalProps> = ({ open, onSuccess, onFailure, onClose }) => {
	const { t } = useTranslation();
	const [shuffled] = useState<number[]>(() => shuffleNumbers(NUMBERS));
	const [taps, setTaps] = useState<number[]>([]);
	const [isCorrect, setIsCorrect] = useState(false);

	const handleCellTap = useCallback(
		(cellIndex: number) => {
			if (taps.length >= INPUT_LENGTH) return;

			const nextTaps = [...taps, cellIndex];

			setTaps(nextTaps);

			if (nextTaps.length === PATTERN_LENGTH) {
				const matched = CORRECT_PATTERN.every((p, i) => nextTaps[i] === p);

				setIsCorrect(matched);
			} else if (nextTaps.length > PATTERN_LENGTH) {
				// Any input beyond PATTERN_LENGTH fails — entering a 6th tap means the user
				// doesn't know the true PIN length, so we reject even a correct 5-prefix.
				setIsCorrect(false);
			}
		},
		[taps]
	);

	const handleActionBtn = useCallback(() => {
		if (isCorrect) {
			onSuccess();
		} else {
			onFailure?.(taps.length);
			setTaps([]);
			setIsCorrect(false);
		}
	}, [isCorrect, onSuccess, onFailure, taps.length]);

	return (
		<PopupDialog
			open={open}
			data-testid="PinAuthModal"
			className="PinAuthModal"
			content={
				<div className="PinAuthContent">
					<div className="PinAuthDots" data-testid="PinAuthDots">
						{Array.from({ length: INPUT_LENGTH }, (_, i) => (
							<span
								key={i}
								data-testid="pin-dot"
								className={`PinDot${i < taps.length ? " PinDot--filled" : ""}`}
							/>
						))}
					</div>
					<div className="PinAuthGrid" data-testid="PinAuthGrid">
						{shuffled.map((num, idx) => (
							<button
								key={idx}
								className="PinAuthCell"
								data-testid={`PinAuthCell-${idx}`}
								onClick={() => handleCellTap(idx)}
								type="button"
							>
								{num}
							</button>
						))}
					</div>
					<button
						data-testid="pin-action-btn"
						className="PinActionBtn PinActionBtn--ok"
						onClick={handleActionBtn}
						type="button"
					>
						{t("common.ok")}
					</button>
				</div>
			}
			buttons={[
				{
					text: t("common.cancel"),
					onClick: onClose,
					className: "BtnPinAuthCancel",
				},
			]}
		/>
	);
};

export default PinAuthModal;
